import {
  FieldInputValue,
  FormFieldType,
  InputFieldProps,
  yupSchemaToValidationFunc
} from 'components/Forms';
import * as Yup from 'yup';
import {
  CustomerAppointmentType,
  EVFormChannel,
  PaymentType
} from 'models/Appointment';
import { DateTime } from 'luxon';
import {
  MPAN_LENGTH_ERROR_MESSAGE,
  MPAN_NUMBERS_ERROR_MESSAGE,
  MPAN_ZERO_START_ERROR_MESSAGE,
  MPRN_MAX_LENGTH_ERROR_MESSAGE,
  MPRN_MIN_LENGTH_ERROR_MESSAGE,
  MPRN_NUMBERS_ERROR_MESSAGE,
  MPRN_ZERO_START_ERROR_MESSAGE,
  REQUIRED_FIELD_ERROR_MESSAGE
} from 'constants/validation';
import { capitalize } from '@material-ui/core';
import JobType from 'models/JobTypes';
import keycloak, { hasRole, Role } from 'auth/keycloak';
import { Availability, Slot } from 'models/Availability';
import { API_TIME_FORMAT, SHORT_TIME_FORMAT } from 'constants/dates';
import { DispatchDay, JobFormFieldName } from './immediateDispatch';

export { DispatchDay, JobFormFieldName };

export const renderedSlotOptions = (slots: Slot[]) =>
  slots.map(slot => {
    const timeRange = `${slot.startTime}-${slot.endTime}`;
    return {
      value: timeRange,
      label: timeRange,
      key: timeRange
    } as FieldInputValue;
  });

export const disableDate = (
  date: DateTime,
  availableDates: string[]
): boolean => !availableDates.includes(date.toISODate());

export const minimalField = (
  id: string,
  label: string,
  inputType: FormFieldType = 'textfield'
) => {
  return {
    id,
    label,
    name: id,
    qaId: `addJob${capitalize(id)}`,
    inputType
  };
};

export const buildJobFormFields = (
  availabilityData: Availability[],
  selectedDate: DateTime,
  setSelectedDate: (date: DateTime) => void,
  bookingType: CustomerAppointmentType | null | undefined
): InputFieldProps[][] => {
  const availabilityDates = availabilityData.reduce<string[]>(
    (newDates, data) =>
      data.isAvailable && !newDates.includes(data.date)
        ? [...newDates, data.date]
        : newDates,
    []
  );

  const selectedAvailability = selectedDate
    ? availabilityData.filter(
        availability => availability.date === selectedDate.toISODate()
      )
    : null;

  const bypassCapacityCheck = hasRole(keycloak, Role.BypassCapacityCheck);

  const bypassCapacityCheckField = {
    ...minimalField(
      JobFormFieldName.ImmediateDispatch,
      'Immediate dispatch',
      'checkbox'
    ),
    labelPlacement: 'start'
  };

  const today = DateTime.now();
  const tomorrow = today.plus({ days: 1 });

  const immediateDispatchDay = {
    ...minimalField(
      JobFormFieldName.ImmediateDispatchDay,
      'Immediate dispatch date',
      'radio'
    ),
    options: [
      {
        value: DispatchDay.Today,
        label: `Today, ${today.toFormat('dd MMM yyyy')}`,
        key: DispatchDay.Today
      },
      {
        value: DispatchDay.Tomorrow,
        label: `Tomorrow, ${tomorrow.toFormat('dd MMM yyyy')}`,
        key: DispatchDay.Tomorrow
      }
    ],
    conditional: {
      when: JobFormFieldName.ImmediateDispatch,
      is: true
    }
  };

  const accountDetailsFieldsSmart = [
    minimalField('accountNumber', 'Account number'),
    {
      ...minimalField('paymentType', 'Payment type', 'select'),
      options: Object.values(PaymentType).map(type => ({
        value: type,
        label: type,
        key: type
      }))
    },
    minimalField('mpan', 'MPAN'),
    minimalField('mprn', 'MPRN'),
    minimalField('electricityMsn', 'Electricity MSN'),
    minimalField('gasMsn', 'Gas MSN'),
    minimalField('bookingNotes', 'Booking notes')
  ];

  const accountDetailsFieldsEV = [
    minimalField('zohoAccountId', 'Zoho account n.'),
    {
      ...minimalField('channel', 'Channel', 'select'),
      options: Object.entries(EVFormChannel).map(
        ([channelId, channelDisplayName]) => ({
          value: channelId,
          label: channelDisplayName,
          key: channelId
        })
      )
    },
    {
      id: 'jumptechURL',
      label: 'Jumptech URL',
      name: 'jumptechURL',
      qaId: `addJobJumptechURL`,
      inputType: 'textfield' as FormFieldType,
      conditional: {
        when: 'channel',
        isIn: ['OVO', 'INDRA', 'HYPERVOLT']
      }
    },
    minimalField('mpan', 'MPAN'),
    minimalField('bookingNotes', 'Booking notes')
  ];

  const accountDetailsFieldsEvIsolator = [
    minimalField('accountNumber', 'OVO Account number'),
    {
      ...minimalField('channel', 'Channel', 'select'),
      options: Object.entries(EVFormChannel).map(
        ([channelId, channelDisplayName]) => ({
          value: channelId,
          label: channelDisplayName,
          key: channelId
        })
      )
    },
    minimalField('mpan', 'MPAN'),
    minimalField('electricityMsn', 'Elec MSN'),
    minimalField('bookingNotes', 'Booking notes')
  ];

  function getAvailabilitySlotOptions(availability: Availability[]) {
    return availability
      .filter(a => a.isAvailable)
      .map(slot => {
        const formattedStartTime = DateTime.fromFormat(
          slot.startTime,
          API_TIME_FORMAT
        );
        const formattedEndTime = DateTime.fromFormat(
          slot.endTime,
          API_TIME_FORMAT
        );

        const timeRange = `${formattedStartTime.toFormat(
          SHORT_TIME_FORMAT
        )}-${formattedEndTime.toFormat(SHORT_TIME_FORMAT)}`;
        return {
          value: timeRange,
          label: timeRange,
          key: timeRange
        } as FieldInputValue;
      });
  }

  function getTimeSlotOptions() {
    return (
      selectedAvailability && getAvailabilitySlotOptions(selectedAvailability)
    );
  }

  return [
    [
      ...(bypassCapacityCheck
        ? [bypassCapacityCheckField, immediateDispatchDay]
        : []),
      {
        ...minimalField('date', 'Date', 'date'),
        shouldDisableDate: (date: DateTime) =>
          disableDate(date, availabilityDates),
        handleChange: (date: DateTime) => setSelectedDate(date)
      },
      {
        ...minimalField('timeSlot', 'Time slot', 'select'),
        options: getTimeSlotOptions()
      }
    ],
    [
      minimalField('firstName', 'First name'),
      minimalField('lastName', 'Last name'),
      minimalField('phoneNumber', 'Phone number'),
      minimalField('secondaryNumber', 'Secondary number'),
      minimalField('addressLine1', 'Address line 1'),
      minimalField('addressLine2', 'Address line 2'),
      minimalField('addressLine3', 'Address line 3'),
      minimalField('city', 'City')
    ],
    (() => {
      switch (bookingType) {
        case CustomerAppointmentType.EV:
          return accountDetailsFieldsEV;
        case CustomerAppointmentType.EV_ISOLATOR:
          return accountDetailsFieldsEvIsolator;
        default:
          return accountDetailsFieldsSmart;
      }
    })()
  ];
};

export enum VulnerabilitiesFieldNames {
  CustomerHasVulnerabilities = 'customerHasVulnerabilities',
  IsOver65 = 'isOver65',
  HasDisability = 'hasDisability',
  HasHearingDifficulties = 'hasHearingDifficulties',
  HasSpeechDifficulties = 'hasSpeechDifficulties',
  HasSmellDifficulties = 'hasSmellDifficulties',
  IsBlindOrPartiallySighted = 'isBlindOrPartiallySighted',
  HasSeriousIllness = 'hasSeriousIllness',
  SpeaksOtherLanguage = 'speaksOtherLanguage',
  IsReliantOnMedicalEquipment = 'isReliantOnMedicalEquipment',
  HasYoungChildren = 'hasYoungChildren',
  HasOtherVulnerability = 'hasOtherVulnerability',
  OtherVulnerability = 'other'
}

export const vulnerabilitiesField = {
  ...minimalField(
    VulnerabilitiesFieldNames.CustomerHasVulnerabilities,
    'Does this customer have any vulnerabilities?',
    'radio'
  ),
  options: [
    { value: 'true', label: 'Yes', key: 'true' },
    { value: 'false', label: 'No', key: 'false' }
  ]
};

export const otherVulnerabilityField = minimalField('other', 'Comments');

export const vulnerabilitiesLabelMap = {
  [VulnerabilitiesFieldNames.IsOver65]: 'Customer is over 65 years old',
  [VulnerabilitiesFieldNames.HasDisability]:
    'Customer has a physical disability',
  [VulnerabilitiesFieldNames.HasHearingDifficulties]:
    'Customer has hearing difficulties',
  [VulnerabilitiesFieldNames.HasSpeechDifficulties]:
    'Customer has speech difficulties',
  [VulnerabilitiesFieldNames.HasSmellDifficulties]:
    'Customer has a poor sense of smell',
  [VulnerabilitiesFieldNames.IsBlindOrPartiallySighted]:
    'Customer is blind or partially sighted',
  [VulnerabilitiesFieldNames.HasSeriousIllness]:
    'Customer has a serious illness',
  [VulnerabilitiesFieldNames.SpeaksOtherLanguage]:
    'Customer prefers to speak in a language other than English',
  [VulnerabilitiesFieldNames.IsReliantOnMedicalEquipment]:
    'Customer relies on medical equipment',
  [VulnerabilitiesFieldNames.HasYoungChildren]: 'Customer has young children',
  [VulnerabilitiesFieldNames.HasOtherVulnerability]: 'Other'
};

const vulnerabiltiesFields = {
  [VulnerabilitiesFieldNames.CustomerHasVulnerabilities]: Yup.string()
    .oneOf(['true', 'false'])
    .required(REQUIRED_FIELD_ERROR_MESSAGE),
  [VulnerabilitiesFieldNames.OtherVulnerability]: Yup.string().when(
    'hasOtherVulnerability',
    {
      is: true,
      then: Yup.string().required(REQUIRED_FIELD_ERROR_MESSAGE)
    }
  )
};

const customerDetailsFieldsValidation = Yup.object().shape({
  firstName: Yup.string().required(REQUIRED_FIELD_ERROR_MESSAGE),
  lastName: Yup.string().required(REQUIRED_FIELD_ERROR_MESSAGE),
  phoneNumber: Yup.number()
    .typeError('Must be a number')
    .required(REQUIRED_FIELD_ERROR_MESSAGE),
  secondaryNumber: Yup.number().typeError('Must be a number'),
  addressLine1: Yup.string().required(REQUIRED_FIELD_ERROR_MESSAGE),
  city: Yup.string().required(REQUIRED_FIELD_ERROR_MESSAGE)
});

const accountDetailsFieldsValidationSmart = () =>
  Yup.object().shape({
    mpan: Yup.string()
      .transform(val => val.replace(/ /g, ''))
      .length(13, MPAN_LENGTH_ERROR_MESSAGE)
      .matches(/^[^0].*$/, MPAN_ZERO_START_ERROR_MESSAGE)
      .matches(/^\d+$/, MPAN_NUMBERS_ERROR_MESSAGE),
    mprn: Yup.string()
      .transform(val => val.replace(/ /g, ''))
      .min(6, MPRN_MIN_LENGTH_ERROR_MESSAGE)
      .max(10, MPRN_MAX_LENGTH_ERROR_MESSAGE)
      .matches(/^[^0].*$/, MPRN_ZERO_START_ERROR_MESSAGE)
      .matches(/^\d+$/, MPRN_NUMBERS_ERROR_MESSAGE),
    accountNumber: Yup.string().required(REQUIRED_FIELD_ERROR_MESSAGE),
    paymentType: Yup.string().required(REQUIRED_FIELD_ERROR_MESSAGE)
  });

export const jumptechUrlRegex = /^.*https?:\/\/ovo-drive\.jumptech\.co\.uk\/project\/(?<jumptechProjectId>[a-fA-F0-9-]+).*$/;

const accountDetailsFieldsValidationEV = Yup.object().shape({
  mpan: Yup.string()
    .transform(val => val.replace(/ /g, ''))
    .length(13, MPAN_LENGTH_ERROR_MESSAGE)
    .matches(/^[^0].*$/, MPAN_ZERO_START_ERROR_MESSAGE)
    .matches(/^\d+$/, MPAN_NUMBERS_ERROR_MESSAGE),
  channel: Yup.string().required(REQUIRED_FIELD_ERROR_MESSAGE),
  jumptechURL: Yup.string().when('channel', {
    is: (channel: any) => ['OVO', 'INDRA', 'HYPERVOLT'].includes(channel),
    then: Yup.string().matches(
      jumptechUrlRegex,
      'Please enter a valid Jumptech URL (https://ovo-drive.jumptech.co.uk/project/project-id)'
    )
  })
});

const accountDetailsFieldsValidationEvIsolator = Yup.object().shape({
  mpan: Yup.string()
    .required(REQUIRED_FIELD_ERROR_MESSAGE)
    .transform(val => val.replace(/ /g, ''))
    .length(13, MPAN_LENGTH_ERROR_MESSAGE)
    .matches(/^[^0].*$/, MPAN_ZERO_START_ERROR_MESSAGE)
    .matches(/^\d+$/, MPAN_NUMBERS_ERROR_MESSAGE),
  channel: Yup.string().required(REQUIRED_FIELD_ERROR_MESSAGE),
  electricityMsn: Yup.string().required(REQUIRED_FIELD_ERROR_MESSAGE),
  accountNumber: Yup.string().required(REQUIRED_FIELD_ERROR_MESSAGE)
});

export interface JobTypeData {
  id: string | null;
  name: string | null;
  extraAttributes: JobType['extraAttributes'] | null;
}

const appointmentDetailsFieldsValidation = () =>
  Yup.object().shape({
    date: Yup.string().nullable(),
    timeSlot: Yup.string().required(REQUIRED_FIELD_ERROR_MESSAGE)
  });

const addJobFormFieldsValidationSmart = () =>
  appointmentDetailsFieldsValidation()
    .concat(customerDetailsFieldsValidation)
    .concat(accountDetailsFieldsValidationSmart());

const addJobFormFieldsValidationEV = () =>
  appointmentDetailsFieldsValidation()
    .concat(customerDetailsFieldsValidation)
    .concat(accountDetailsFieldsValidationEV);

const addJobFormFieldsValidationEvIsolator = () =>
  appointmentDetailsFieldsValidation()
    .concat(customerDetailsFieldsValidation)
    .concat(accountDetailsFieldsValidationEvIsolator);

export const addJobFormValidation = (
  bookingType: CustomerAppointmentType | undefined | null
) => {
  const vulnerabilitiesSchema = Yup.object()
    .shape(vulnerabiltiesFields)
    .test('dateIsNotRequired', function validateDateIsNotRequired({
      date,
      [JobFormFieldName.ImmediateDispatch]: isImmediateDispatch
    }) {
      if (!isImmediateDispatch && !date) {
        return this.createError({
          message: REQUIRED_FIELD_ERROR_MESSAGE,
          path: 'date'
        });
      }
      return true;
    })
    .test('vulnerabilitiesAreValid', function validateVulnerabilitiesTest({
      customerHasVulnerabilities,
      isOver65,
      hasDisability,
      hasHearingDifficulties,
      hasSpeechDifficulties,
      hasSmellDifficulties,
      isBlindOrPartiallySighted,
      hasSeriousIllness,
      speaksOtherLanguage,
      isReliantOnMedicalEquipment,
      hasYoungChildren,
      hasOtherVulnerability
    }) {
      if (
        customerHasVulnerabilities === 'true' &&
        !isOver65 &&
        !hasDisability &&
        !hasHearingDifficulties &&
        !hasSpeechDifficulties &&
        !hasSmellDifficulties &&
        !isBlindOrPartiallySighted &&
        !hasSeriousIllness &&
        !speaksOtherLanguage &&
        !isReliantOnMedicalEquipment &&
        !hasYoungChildren &&
        !hasOtherVulnerability
      ) {
        return this.createError({
          message: 'At least one of these fields is required',
          path: 'vulnerabilitiesGroup'
        });
      }
      return true;
    });

  const validationSchema = (() => {
    switch (bookingType) {
      case CustomerAppointmentType.EV:
        return addJobFormFieldsValidationEV();
      case CustomerAppointmentType.EV_ISOLATOR:
        return addJobFormFieldsValidationEvIsolator();
      case CustomerAppointmentType.MAINTENANCE:
      case CustomerAppointmentType.SMETS2:
      default:
        return addJobFormFieldsValidationSmart();
    }
  })();

  return yupSchemaToValidationFunc(
    vulnerabilitiesSchema.concat(validationSchema)
  );
};
